// Copyright 2023 Anthony Howell. All rights reserved.
import { observer } from 'mobx-react';
import ViewModel from './core/ViewModel';
import { GameType, GameTypeExtensions } from './core/types';
import './GameModifiersView.scss';

interface GameModifiersViewProps {
  viewModel: ViewModel;
}

const GameModifiersView = observer(({ viewModel }: GameModifiersViewProps) => {
  const handleNumberOfPlayersChange = (change: number) => {
    const newNumberOfPlayers = viewModel.numberOfPlayers + change;
    if (
      newNumberOfPlayers >= 2 &&
      newNumberOfPlayers <= GameTypeExtensions[viewModel.selectedGameType].maxNumberOfPlayers
    ) {
      viewModel.setNumberOfPlayers(newNumberOfPlayers);
    }
  };

  const handleTwoBoardsChange = () => {
    viewModel.setEnableTwoBoards(!viewModel.enableTwoBoards);
  };

  const handleGameTypeChange = (gameType: GameType) => {
    viewModel.setSelectedGameType(gameType);
  };

  const handleNumberOfSimulationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    viewModel.setNumberOfSimulations(value);
  };

  return (
    <div className="game-modifiers">
      <div className="two-boards-selector">
        <button
          className="two-boards-button"
          onClick={handleTwoBoardsChange}
        >
          {viewModel.enableTwoBoards ? 'Disable Double Board' : 'Enable Double Board'}
        </button>
      </div>

      {/* Simulations */}
      <div className="simulation-controls">
        <div className="simulation-inputs">
          <div className="number-of-simulations-selector">
            <label htmlFor="numberOfSimulations">Simulations:</label>
            <input
              type="range"
              id="numberOfSimulations"
              min={1}
              max={5000}
              step={1}
              value={viewModel.numberOfSimulations}
              onChange={handleNumberOfSimulationsChange}
            />
            <span>{viewModel.numberOfSimulations}</span>
          </div>
        </div>
      </div>

      {/* Game Type */}
      <div className="game-type-selector">
        {Object.values(GameType).map((gameType) => (
          <button
            key={gameType}
            onClick={() => handleGameTypeChange(gameType)}
            className={`game-type-button ${
              viewModel.selectedGameType === gameType ? 'selected' : ''
            }`}
          >
            {gameType}
          </button>
        ))}
      </div>

      {/* Number of Players */}
      <div className="number-of-players-selector">
        <p>Players</p>
        <button className="player-count-button" onClick={() => handleNumberOfPlayersChange(-1)}>
          -
        </button>
        <span className="player-count">{viewModel.numberOfPlayers}</span>
        <button className="player-count-button" onClick={() => handleNumberOfPlayersChange(1)}>
          +
        </button>
      </div>
    </div>
  );
});

export default GameModifiersView;
